/** @internal – import from `@/store/useDepartmentStore` instead. */
import { runSSEStream } from "../sseRunner";
import { sseEvents } from "../../../events";

/**
 * Social-video generation SSE pipeline runner. Posts to
 * `/core/coagent/social-video-generate` and publishes typed
 * `social-video:*` events.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 79.
 */
export async function runSocialVideoGenerateSSE(
  input: {
    chatId: string;
    source: "template" | "custom" | "brief";
    templateId?: string;
    briefId?: string;
    aspectRatio: "16:9" | "9:16";
    prompt?: string;
    overlayText?: string;
  },
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  await runSSEStream(input, {
    pipeline: "social-video-generate",
    endpoint: "/core/coagent/social-video-generate",
    sessionId,
    buildBody: (sid, inp) => ({ sessionId: sid, ...inp }),
    errorPrefix: "Video generation",
    signal,
    debugMeta: { source: input.source },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error } = event as Record<string, any>;

      if (phase === "input_validated") {
        sseEvents.publish({
          type: "social-video:input-validated",
          pipeline: "social-video-generate",
          sessionId,
        });
        return;
      }

      if (phase === "prompt_compiled") {
        sseEvents.publish({
          type: "social-video:prompt-compiled",
          pipeline: "social-video-generate",
          sessionId,
        });
        return;
      }

      if (phase === "video_generation" && status === "running") {
        sseEvents.publish({
          type: "social-video:video-generation-running",
          pipeline: "social-video-generate",
          sessionId,
          message:
            typeof data?.message === "string" ? data.message : "Generating video with Veo...",
        });
        return;
      }

      if (phase === "video_download") {
        sseEvents.publish({
          type: "social-video:video-download",
          pipeline: "social-video-generate",
          sessionId,
        });
        return;
      }

      if (phase === "done" && data?.video) {
        sseEvents.publish({
          type: "social-video:done",
          pipeline: "social-video-generate",
          sessionId,
          video: data.video,
        });
        return;
      }

      if (phase === "error") {
        const message = typeof error === "string" ? error : "Video generation failed";
        sseEvents.publish({
          type: "social-video:error",
          pipeline: "social-video-generate",
          sessionId,
          message,
        });
      }
    },
  });
}
